<template>
  <div class="user-menu">
    <i-menu
      v-if="currentUser"
      ref="menuEl"
      :close-on-item-click="true"
      :trigger-align="iMenuTriggerAlignOptions.RIGHT"
      :width="212"
      @i-menu-open-changed="onMenuOpenChanged"
    >
      <template #trigger>
        <button
          data-testid="user-button"
          :class="{
            open: menuOpen === true,
            collapsed,
            'i-p-6': !collapsed,
            'd-flex align-items-center justify-content-center': collapsed,
          }"
          class="user-button i-radius-2xl"
          @mouseenter="hovering = true"
          @mouseleave="hovering = ''"
        >
          <div
            :class="{ 'align-items-center justify-content-center': collapsed }"
            class="user-container d-flex flex-row align-items-center"
          >
            <user-avatar
              :user="currentUser"
              :class="{ 'i-mr-5': !collapsed }"
            ></user-avatar>
            <div class="user-data d-flex flex-column align-items-start">
              <div
                class="name data text-neutral-bold i-text-button-3"
                :title="username"
              >
                {{ username }}
              </div>
              <div
                class="organisation data text-neutral i-text-body-3"
                :title="organisation"
              >
                {{ organisation }}
              </div>
            </div>
            <i-icon
              :symbol="menuOpen ? 'small-chevron-up' : 'small-chevron-down'"
              class="menu-icon"
              :colour="
                hovering || menuOpen
                  ? 'var(--icon-neutral)'
                  : 'var(--icon-neutral)'
              "
              :size="16"
            ></i-icon>
          </div>
        </button>
      </template>

      <template #items>
        <i-menu-item
          label="Account"
          icon="small-user-single"
          data-testid="user-menu-account"
          @item-click="() => openRoute('/settings/account')"
        ></i-menu-item>

        <i-menu-item
          label="Team"
          icon="small-user-multiple"
          data-testid="user-menu-team"
          @item-click="() => openRoute('/settings/team')"
        ></i-menu-item>

        <i-menu-item
          v-if="hasEditPermissions"
          label="Invite User"
          icon="small-user-add"
          data-testid="user-menu-invite-user"
          @item-click="() => inviteUser()"
        ></i-menu-item>

        <div class="divider"></div>

        <i-menu-item
          label="Help Center"
          icon="small-help-question-circle"
          data-testid="user-menu-help-center"
          right-icon="medium_link_square_1"
          :right-icon-color="'var(--icon-neutral)'"
          @item-click="() => openExternalLink('https://help.inscribe.ai/en/')"
        ></i-menu-item>

        <i-menu-item
          label="API Documentation"
          icon="small-programming-browser-code-2"
          data-testid="user-menu-api-documentation"
          right-icon="medium_link_square_1"
          :right-icon-color="'var(--icon-neutral)'"
          @item-click="() => openExternalLink('https://docs.inscribe.ai')"
        ></i-menu-item>

        <div class="divider"></div>

        <!-- workspaces -->
        <i-menu-item
          v-for="ws in firstFiveWorkspaces"
          :key="ws.id"
          :label="ws.name"
          :checked="ws.isCurrentWorkspace"
          icon="small-dashboard-1"
          data-testid="user-menu-workspaces"
          @item-click="() => switchToWorkspace(ws)"
        ></i-menu-item>

        <i-menu-item
          v-if="tooManyWorkSpaces"
          label="Switch Workspace"
          icon="small-arrows-reload"
          data-testid="user-menu-switch-workspace"
          @item-click="() => (showSwitcherModal = true)"
        ></i-menu-item>

        <i-menu-item
          v-if="orgAdminAndCreationEnabled"
          label="New Workspace"
          icon="small-add-circle"
          data-testid="user-menu-new-workspace"
          @item-click="() => showCreateNewWorkspace()"
        ></i-menu-item>

        <div class="divider"></div>

        <i-menu-item
          label="Sign Out"
          icon="small-logout"
          data-testid="user-menu-logout"
          @item-click="() => doLogout()"
        ></i-menu-item>
      </template>
    </i-menu>

    <!-- workspace panel  -->
    <workspace-switcher
      :show-switcher-modal="showSwitcherModal"
      :show-creation-modal="showCreationModal"
      @toggle-switcher-modal="toggleSwitcherModal()"
      @set-creation-modal-open="onSetCreationModalOpen"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  iIcon,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  showToast,
} from '@inscribe/signal';
import UserAvatar from '@/V2/components/UserAvatar';
import WorkspaceSwitcher from './WorkspaceSwitcher';

export default {
  name: 'UserArea',
  components: {
    iIcon,
    iMenu,
    iMenuItem,
    UserAvatar,
    WorkspaceSwitcher,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    iMenuTriggerAlignOptions,
    hovering: false,
    menuOpen: false,
    showSwitcherModal: false,
    showCreationModal: false,
  }),
  computed: {
    ...mapGetters({
      orgAdminAndCreationEnabled: 'isOrgAdminAndWorkspacesCreationEnabled',
      hasEditPermissions: 'isOrgAdmin',
    }),
    ...mapState(['currentUser', 'workspaces']),
    username() {
      if (this.currentUser.first_name || this.currentUser.last_name) {
        return `${this.currentUser.first_name} ${this.currentUser.last_name}`;
      } else {
        return this.currentUser.username;
      }
    },
    organisation() {
      return this.currentUser.organisation?.name || '';
    },
    firstFiveWorkspaces() {
      return this.tooManyWorkSpaces
        ? []
        : this.workspaces
            .map((org) => {
              return {
                name: org.name,
                id: org.id,
                isCurrentWorkspace:
                  org.name === this.currentUser.organisation.name,
                button: true,
              };
            })
            .sort((a, b) => b.isCurrentWorkspace - a.isCurrentWorkspace);
    },
    tooManyWorkSpaces() {
      return this.workspaces.length > 5;
    },
  },
  methods: {
    ...mapActions(['switchWorkspace']),
    openRoute(path) {
      if (this.$route.fullPath !== path) {
        this.$router.push({
          path: path,
        });
      }
    },
    inviteUser() {
      const route = '/settings/team?showInviteModal=true';
      if (!this.$route.fullPath.includes('/settings/team')) {
        this.openRoute(route);
      } else {
        this.openRoute('/');
        setTimeout(() => {
          this.openRoute(route);
        });
      }
    },
    openExternalLink(url) {
      window.open(url, '_blank');
    },
    doLogout() {
      if (confirm('Are you sure you want to log out?')) {
        localStorage.removeItem('inscribeScopeSelectedUserDefault');
        this.$store.dispatch('logout').then(() => {
          this.$router.push({ name: 'login' });
        });
      }
    },
    toggleSwitcherModal() {
      this.showSwitcherModal = !this.showSwitcherModal;
    },
    onMenuOpenChanged(menuOpen) {
      this.menuOpen = menuOpen;
    },
    onSetCreationModalOpen(value) {
      this.showCreationModal = value;
    },
    showCreateNewWorkspace() {
      this.showSwitcherModal = false;
      this.showCreationModal = true;
    },
    switchToWorkspace(workspace) {
      if (!workspace.isCurrentWorkspace) {
        this.switchWorkspace(workspace.id)
          .then((response) => {
            const { redirect_url } = response.data;
            window.location.href = redirect_url || '/';
            if (!redirect_url || !redirect_url.includes('?sso=1')) {
              window.location.reload(); // reload so it uses new data
            }
          })
          .catch(() => {
            showToast({
              text: 'Error switching workspace.',
              isError: true,
            });
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-menu {
  user-select: none;

  :deep(.trigger-container) {
    display: flex;
    justify-content: center;
  }

  .user-button {
    width: 100%;
    height: 56px;
    border: 1px solid var(--border-neutral-subtle);
    background-color: var(--surface-neutral-subtle);
    outline: 0;
    transition: background-color 150ms ease-in-out;

    &:hover,
    &.open {
      background-color: var(--base);
    }

    &.collapsed {
      height: 34px;
      width: 34px;
      border-radius: 50%;

      .user-data,
      .menu-icon {
        display: none !important;
      }
    }

    .user-data {
      flex: 1;

      .data {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 120px;
      }
    }
  }
}
</style>
